import { useMutation } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';
import { useToast } from 'components/hooks/useToast';

import { OKR_TYPES, TOAST_STATUSES } from 'constants/ProjectConstants';
import { endpoints } from 'constants/endpoints';

const generateTitleForOKRApi = ({
  parentPost = null,
  uuid,
  type,
  toIds = [],
  description = '',
  text = '',
  measure,
  measureType,
  target,
  startingProgressValue,
  topicsSelected,
  recommendationCount,
  userPrompt,
  ...rest
}) => {
  const url = endpoints.rabbitGoals.generator;
  return sendRequest({
    method: 'POST',
    url,
    body: {
      parentPost,
      uuid,
      toIds,
      description,
      text,
      measure,
      measureType,
      target,
      startingProgressValue,
      topicsSelected,
      recommendationCount,
      serviceName: type === OKR_TYPES.OBJECTIVE ? 'goalObjectiveTitleGeneration' : 'goalKRTitleGeneration',
      userPrompt: userPrompt || undefined,
      ...rest,
    },
    credentials: 'include',
  });
};

export const useGenerateTitleForOKRMutation = ({ onUpdate = () => {}, onError = () => {} }) =>
  useMutation({
    mutationFn: ({ payload }) => generateTitleForOKRApi(payload),
    onError: (err) => {
      const isUnexpectedError = err?.message?.includes('Unexpected end of JSON input');
      let error = null;
      if (isUnexpectedError) {
        error = { message: 'Uh-oh! Looks like an unknown error happened.' };
      } else {
        error = err?.body;
      }
      onError({ err: error });
    },
    onSuccess: (data, variables) => {
      if (typeof onUpdate === 'function') {
        onUpdate(data?.entity, variables);
      }
    },
  });

const generateTrackingMetricsForOKRApi = ({
  parentPost = null,
  uuid,
  toIds = [],
  description = '',
  text = '',
  measure,
  measureType,
  target,
  startingProgressValue,
  topicsSelected,
  recommendationCount,
  userPrompt,
  ...rest
}) => {
  const url = endpoints.rabbitGoals.generatorTracking;

  return sendRequest({
    method: 'POST',
    url,
    body: {
      parentPost,
      uuid,
      toIds,
      description,
      text,
      measure,
      measureType,
      target,
      startingProgressValue,
      topicsSelected,
      recommendationCount,
      serviceName: 'goalTrackingGeneration',
      userPrompt,
      ...rest,
    },
    credentials: 'include',
  });
};

export const useGenerateTrackingMetricsForOKRMutation = ({ onUpdate = () => {}, onError = () => {} }) =>
  useMutation({
    mutationFn: ({ payload }) => generateTrackingMetricsForOKRApi(payload),
    onError: (err) => {
      const isUnexpectedError = err?.message?.includes('Unexpected end of JSON input');
      let error = null;
      if (isUnexpectedError) {
        error = { message: 'Uh-oh! Looks like an unknown error happened.' };
      } else {
        error = err?.body;
      }

      onError({ err: error });
    },
    onSuccess: (data, variables) => {
      if (typeof onUpdate === 'function') {
        onUpdate(data?.entity, variables);
      }
    },
  });

const saveFeedbackApi = ({
  userAction = null,
  requestId = null,
  recommendedContent = undefined,
  usedContent = undefined,
  serviceName,
  revisionNumber,
}) => {
  const url = endpoints.rabbitGoals.feedback;

  return sendRequest({
    method: 'POST',
    url,
    body: {
      userAction,
      requestId,
      recommendedContent,
      usedContent,
      serviceName,
      revisionNumber,
    },
    credentials: 'include',
  });
};

export const useSaveFeedbackApiMutation = ({ onUpdate = () => {}, onError = () => {} }) => {
  const toast = useToast();

  return useMutation({
    mutationFn: ({ payload }) => saveFeedbackApi(payload),
    onError: (err) => {
      const isUnexpectedError = err?.message?.includes('Unexpected end of JSON input');
      let error = null;
      if (isUnexpectedError) {
        error = { message: 'Uh-oh! Looks like an unknown error happened.' };
      } else {
        error = err?.body;
      }
      toast({
        title: error.message || 'Uh-oh! Looks like an unknown error happened.',
        status: TOAST_STATUSES.ERROR,
      });
      onError({ err: error });
    },
    onSuccess: (data, variables) => {
      if (typeof onUpdate === 'function') {
        onUpdate(data?.entity, variables);
      }
    },
  });
};

const generateTitleAndMetricForOKRApi = ({
  parentPost = null,
  uuid,
  type,
  toIds = [],
  description = '',
  text = '',
  measure,
  measureType,
  target,
  startingProgressValue,
  topicsSelected,
  recommendationCount,
  userPrompt,
  ...rest
}) => {
  const url = endpoints.rabbitGoals.generateRecommendation;

  return sendRequest({
    method: 'POST',
    url,
    body: {
      parentPost,
      uuid,
      toIds,
      description,
      text,
      measure,
      measureType,
      target,
      startingProgressValue,
      topicsSelected,
      recommendationCount,
      serviceName: type === OKR_TYPES.OBJECTIVE ? 'goalObjectiveGeneration' : 'goalKRGeneration',
      userPrompt: userPrompt || undefined,
      ...rest,
    },
    credentials: 'include',
  });
};

export const useGenerateRecommendationForOKRMutation = ({ onUpdate = () => {}, onError = () => {} }) =>
  useMutation({
    mutationFn: ({ payload }) => generateTitleAndMetricForOKRApi(payload),
    onError: (err) => {
      const isUnexpectedError = err?.message?.includes('Unexpected end of JSON input');
      let error = null;
      if (isUnexpectedError) {
        error = { message: 'Uh-oh! Looks like an unknown error happened.' };
      } else {
        error = err?.body;
      }
      onError({ err: error });
    },
    onSuccess: (data, variables) => {
      if (typeof onUpdate === 'function') {
        onUpdate(data?.entity, variables);
      }
    },
  });
